import React, { useEffect, useState, useCallback, useRef } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { getDemoContactSettings } from "../demo/demoContactDetails";
import {
  FaBell,
  FaHome,
  FaChartLine,
  FaEnvelope,
  FaTimes,
  FaCheck,
  FaMapMarkerAlt,
  FaBan,
  FaPhone,
} from "react-icons/fa";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";

export default function ContactNotificationModal({ show, onClose, contact, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Notification toggles
  const [propertyUpdates, setPropertyUpdates] = useState(true);
  const [marketUpdates, setMarketUpdates] = useState(true);

  // Property updates
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propertyFrequency, setPropertyFrequency] = useState("monthly");

  // Market updates
  const [selectedSuburbs, setSelectedSuburbs] = useState([]);
  const [marketFrequency, setMarketFrequency] = useState("monthly");

  const settingsLoadedRef = useRef(false);

  const contactEmail = contact?.email;
  const contactMobile = contact?.clean_mobile || contact?.mobile;
  const contactIdentifier = contactEmail || contactMobile;
  const contactName = [contact?.first_name, contact?.last_name].filter(Boolean).join(" ") || contactIdentifier || "Contact";

  // Load existing settings
  useEffect(() => {
    if (!show || !contactIdentifier) return;
    settingsLoadedRef.current = false;
    setLoading(true);
    setSaved(false);

    // Reset to defaults so previous contact's selections don't leak through
    // when the API response omits a field.
    setPropertyUpdates(true);
    setMarketUpdates(true);
    setSelectedProperties([]);
    setSelectedSuburbs([]);
    setPropertyFrequency("monthly");
    setMarketFrequency("monthly");

    // Parse a settings payload (real or demo) into the toggle/state fields.
    const applySettings = (n) => {
      const prop = n.property_updates;
      const mkt = n.market_updates;

      if (prop && typeof prop === "object" && "enabled" in prop) {
        setPropertyUpdates(prop.enabled ?? true);
        setPropertyFrequency(prop.frequency || "monthly");
        if (Array.isArray(prop.selected_properties)) setSelectedProperties(prop.selected_properties);
      } else {
        setPropertyUpdates(n.property_updates ?? true);
        setPropertyFrequency(n.property_frequency || n.frequency || "monthly");
        if (Array.isArray(n.selected_properties)) setSelectedProperties(n.selected_properties);
      }

      if (mkt && typeof mkt === "object" && "enabled" in mkt) {
        setMarketUpdates(mkt.enabled ?? true);
        setMarketFrequency(mkt.frequency || "monthly");
        if (Array.isArray(mkt.selected_suburbs)) setSelectedSuburbs(mkt.selected_suburbs);
      } else {
        setMarketUpdates(n.suburb_market_updates ?? true);
        setMarketFrequency(n.market_frequency || n.frequency || "monthly");
        if (Array.isArray(n.selected_suburbs)) setSelectedSuburbs(n.selected_suburbs);
      }
    };

    // Demo mode: serve this contact's seeded notification preferences (the
    // backend read is blocked by the demo guard). Falls back to defaults
    // (all on, monthly) for contacts without a seeded sample.
    if (typeof window !== "undefined" && window.__demoMode) {
      const demoSettings = getDemoContactSettings(contactEmail || contactMobile);
      if (demoSettings) applySettings(demoSettings);
      setLoading(false);
      setTimeout(() => { settingsLoadedRef.current = true; }, 300);
      return;
    }

    handleTokenAndPostRequest(`${window.api_host}/agent-read-contact-settings`, {
      contact_email: contactEmail || '',
      contact_mobile: contactMobile || '',
    })
      .then((res) => {
        applySettings(res.data || {});
      })
      .catch((err) => {
        console.error("Failed to load contact settings:", err);
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => { settingsLoadedRef.current = true; }, 300);
      });
  }, [show, contactIdentifier]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!contactIdentifier) return;
    setSaving(true);
    setSaved(false);
    try {
      const userSettings = {
        updated_at: Math.floor(Date.now() / 1000),
        property_updates: {
          enabled: propertyUpdates,
          frequency: propertyFrequency,
          selected_properties: selectedProperties.map((p) => ({ label: p.label, value: p.value })),
        },
        market_updates: {
          enabled: marketUpdates,
          frequency: marketFrequency,
          selected_suburbs: selectedSuburbs.map((s) => ({ label: s.label, value: s.value })),
        },
        delivery: { channels: contactEmail ? ["email"] : ["sms"] },
      };
      await handleTokenAndPostRequest(`${window.api_host}/agent-save-contact-settings`, {
        contact_email: contactEmail || '',
        contact_mobile: contactMobile || '',
        user_settings: userSettings,
      });
      setSaved(true);
      if (onSaved) onSaved({ contact_email: contactEmail || '', contact_mobile: contactMobile || '', user_settings: userSettings });
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save contact settings:", err);
    } finally {
      setSaving(false);
    }
  }, [contactIdentifier, contactEmail, contactMobile, propertyUpdates, propertyFrequency, selectedProperties, marketUpdates, marketFrequency, selectedSuburbs, onSaved]);

  // Disable all notifications shortcut
  const handleDisableAll = useCallback(() => {
    setPropertyUpdates(false);
    setMarketUpdates(false);
    setSelectedProperties([]);
    setSelectedSuburbs([]);
  }, []);

  // Search loaders
  const loadSuburbs = useCallback(
    debounce((inputValue, callback) => {
      if (!inputValue || inputValue.length < 2) { callback([]); return; }
      axios
        .get(`${window.api_host}/location-search?q=${encodeURIComponent(inputValue)}`)
        .then((res) => callback(res.data.filter((r) => r.type === "suburb").map((s) => ({ label: s.label, value: s.value }))))
        .catch(() => callback([]));
    }, 400),
    []
  );

  const loadProperties = useCallback(
    debounce((inputValue, callback) => {
      if (!inputValue || inputValue.length < 3) { callback([]); return; }
      axios
        .get(`${window.api_host}/property-search?q=${encodeURIComponent(inputValue)}`)
        .then((res) => callback(res.data.map((p) => ({ label: p.address_text, value: p.id }))))
        .catch(() => callback([]));
    }, 400),
    []
  );

  if (!show) return null;

  const frequencyOptions = [
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
  ];

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 10,
      borderColor: state.isFocused ? "#0d9488" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #0d9488" : "none",
      minHeight: 40,
      fontSize: 13,
      "&:hover": { borderColor: "#0d9488" },
    }),
    input: (provided) => ({
      ...provided,
      fontSize: isMobile ? 16 : 13,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#f0fdfa",
      borderRadius: 6,
      border: "1px solid #99f6e430",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#1a1a1a",
      fontSize: 12,
      fontWeight: 500,
      padding: "1px 4px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#9ca3af",
      borderRadius: "0 6px 6px 0",
      "&:hover": { backgroundColor: "#fef2f2", color: "#dc2626" },
    }),
    placeholder: (provided) => ({ ...provided, textAlign: "left", color: "#c4c4c4", fontSize: 13 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 10001 }),
    menu: (provided) => ({ ...provided, textAlign: "left", borderRadius: 10, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 9999 }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#f0fdfa" : "#fff",
      color: "#1a1a1a",
      fontSize: 13,
    }),
  };

  const sectionLabel = {
    fontSize: 11,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontWeight: 600,
    marginBottom: 8,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "stretch" : "center",
        padding: isMobile ? 0 : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: isMobile ? 0 : 16,
          width: "100%",
          maxWidth: isMobile ? "none" : 520,
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1px solid #f3f4f6",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: "#fff", borderRadius: "16px 16px 0 0", zIndex: 1,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: "#f0fdfa",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FaBell style={{ color: "#0d9488", fontSize: 16 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>Notification Settings</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }}>
                {contactEmail
                  ? <><FaEnvelope style={{ fontSize: 10, marginRight: 4 }} />{contactEmail}</>
                  : <><FaPhone style={{ fontSize: 10, marginRight: 4 }} />{contactMobile}</>
                }
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6,
            color: "#9ca3af", fontSize: 16,
          }}>
            <FaTimes />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: "20px 24px 24px" }}>
            {/* Skeleton: contact banner */}
            <div style={{ background: "#f3f4f6", borderRadius: 10, height: 38, marginBottom: 20, animation: "pulse 1.5s ease-in-out infinite" }} />
            {/* Skeleton: two setting cards */}
            {[0, 1].map(i => (
              <div key={i} style={{
                border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 14,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />
                  <div style={{ width: 120, height: 14, borderRadius: 6, background: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />
                </div>
                <div style={{ width: "100%", height: 38, borderRadius: 8, background: "#f3f4f6", animation: "pulse 1.5s ease-in-out infinite" }} />
              </div>
            ))}
            {/* Skeleton: frequency */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[80, 90, 100].map((w, i) => (
                <div key={i} style={{ width: w, height: 32, borderRadius: 8, background: "#f3f4f6", animation: "pulse 1.5s ease-in-out infinite" }} />
              ))}
            </div>
            {/* Skeleton: buttons */}
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1, height: 40, borderRadius: 10, background: "#f3f4f6", animation: "pulse 1.5s ease-in-out infinite" }} />
              <div style={{ flex: 1, height: 40, borderRadius: 10, background: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />
            </div>
            <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
          </div>
        ) : (
          <div style={{ padding: "20px 24px 24px" }}>
            {/* Contact name banner */}
            <div style={{
              background: "#f9fafb", borderRadius: 10, padding: "10px 14px",
              marginBottom: 20, fontSize: 13, color: "#374151",
            }}>
              Managing notifications for <strong>{contactName}</strong>
            </div>

            {/* Property Updates */}
            <div style={{
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
              padding: 16, marginBottom: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: propertyUpdates ? 14 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: propertyUpdates ? "#f0fdfa" : "#f3f4f6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <FaHome size={14} style={{ color: propertyUpdates ? "#0d9488" : "#9ca3af" }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#111" }}>Property Updates</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>Monthly reports on tracked properties</div>
                  </div>
                </div>
                <label style={{ position: "relative", display: "inline-block", width: 40, height: 22, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={propertyUpdates}
                    onChange={(e) => setPropertyUpdates(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: "absolute", inset: 0, borderRadius: 11,
                    background: propertyUpdates ? "#0d9488" : "#d1d5db",
                    transition: "background 0.2s",
                  }}>
                    <span style={{
                      position: "absolute", top: 2, left: propertyUpdates ? 20 : 2,
                      width: 18, height: 18, borderRadius: "50%", background: "#fff",
                      transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                    }} />
                  </span>
                </label>
              </div>

              {propertyUpdates && (
                <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14 }}>
                  <div style={sectionLabel}>
                    <FaMapMarkerAlt size={9} style={{ marginRight: 4 }} />Properties to track
                  </div>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    loadOptions={loadProperties}
                    value={selectedProperties}
                    onChange={setSelectedProperties}
                    placeholder="Search by address..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue && inputValue.length >= 3 ? "No properties found" : "Type at least 3 characters"
                    }
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />

                  <div style={{ ...sectionLabel, marginTop: 14 }}>Frequency</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {frequencyOptions.map((opt) => {
                      const active = propertyFrequency === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => setPropertyFrequency(opt.value)}
                          style={{
                            flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12,
                            border: `1.5px solid ${active ? "#0d9488" : "#e5e7eb"}`,
                            background: active ? "#f0fdfa" : "#fff",
                            color: active ? "#0d9488" : "#6b7280",
                            fontWeight: active ? 600 : 500, cursor: "pointer",
                            position: "relative",
                          }}
                        >
                          {active && <FaCheck size={7} style={{ position: "absolute", top: 4, right: 6, color: "#0d9488" }} />}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Market Updates */}
            <div style={{
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
              padding: 16, marginBottom: 20,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: marketUpdates ? 14 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: marketUpdates ? "#f0fdfa" : "#f3f4f6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <FaChartLine size={14} style={{ color: marketUpdates ? "#0d9488" : "#9ca3af" }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#111" }}>Market Updates</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>Suburb market trends and insights</div>
                  </div>
                </div>
                <label style={{ position: "relative", display: "inline-block", width: 40, height: 22, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={marketUpdates}
                    onChange={(e) => setMarketUpdates(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: "absolute", inset: 0, borderRadius: 11,
                    background: marketUpdates ? "#0d9488" : "#d1d5db",
                    transition: "background 0.2s",
                  }}>
                    <span style={{
                      position: "absolute", top: 2, left: marketUpdates ? 20 : 2,
                      width: 18, height: 18, borderRadius: "50%", background: "#fff",
                      transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                    }} />
                  </span>
                </label>
              </div>

              {marketUpdates && (
                <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14 }}>
                  <div style={sectionLabel}>
                    <FaMapMarkerAlt size={9} style={{ marginRight: 4 }} />Suburbs to follow
                  </div>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    loadOptions={loadSuburbs}
                    value={selectedSuburbs}
                    onChange={setSelectedSuburbs}
                    placeholder="Search by suburb or postcode..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue && inputValue.length >= 2 ? "No suburbs found" : "Type at least 2 characters"
                    }
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />

                  <div style={{ ...sectionLabel, marginTop: 14 }}>Frequency</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {frequencyOptions.map((opt) => {
                      const active = marketFrequency === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => setMarketFrequency(opt.value)}
                          style={{
                            flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12,
                            border: `1.5px solid ${active ? "#0d9488" : "#e5e7eb"}`,
                            background: active ? "#f0fdfa" : "#fff",
                            color: active ? "#0d9488" : "#6b7280",
                            fontWeight: active ? 600 : 500, cursor: "pointer",
                            position: "relative",
                          }}
                        >
                          {active && <FaCheck size={7} style={{ position: "absolute", top: 4, right: 6, color: "#0d9488" }} />}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={handleDisableAll}
                disabled={saving}
                style={{
                  flex: 0, padding: "10px 16px", borderRadius: 10, fontSize: 13,
                  border: "1px solid #fecaca", background: "#fff",
                  color: "#dc2626", fontWeight: 500, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                  opacity: saving ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                <FaBan size={11} /> Stop All
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, fontSize: 13,
                  border: "none",
                  background: saved ? "#16a34a" : "#0d9488",
                  color: "#fff", fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  opacity: saving ? 0.7 : 1,
                  transition: "background 0.2s",
                }}
              >
                {saved ? <><FaCheck size={11} /> Saved</> : saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
